import { cn, UzBadge } from '@utazzo/ui';
import { ChevronDown } from 'lucide-react';
import React, { useState } from 'react';

import { KIND_META, actionableKindOf } from '../item-kind';
import { transferRouteLabel } from '../transfer-route';

import { BookingDetails } from './BookingDetails';
import { ItemActions, ItemCard, STATUS_META } from './OpsBookingsDrawer';

import type { OpsActionableKind, OpsCaseItem } from '@utazzo/shared';

/**
 * The bookings of one converted lead laid out like the itinerary's pricing
 * breakup: one tinted section per kind (Flights, Hotels, ...) with a summary
 * table, a jump nav on top, and every row opening into the full booking
 * facts + actions (hold / fund / issue / not available) underneath it.
 */

type Tint =
  | 'sky'
  | 'amber'
  | 'rose'
  | 'emerald'
  | 'violet'
  | 'cyan'
  | 'lime'
  | 'orange'
  | 'zinc'
  | 'fuchsia';

const TINT: Record<Tint, { chip: string; total: string; header: string; link: string }> = {
  sky: {
    chip: 'bg-sky-100 text-sky-700 ring-1 ring-sky-200',
    total: 'text-sky-700',
    header: 'bg-sky-50/60',
    link: 'text-sky-700 decoration-sky-300 hover:bg-sky-50',
  },
  amber: {
    chip: 'bg-amber-100 text-amber-700 ring-1 ring-amber-200',
    total: 'text-amber-700',
    header: 'bg-amber-50/60',
    link: 'text-amber-700 decoration-amber-300 hover:bg-amber-50',
  },
  rose: {
    chip: 'bg-rose-100 text-rose-700 ring-1 ring-rose-200',
    total: 'text-rose-700',
    header: 'bg-rose-50/60',
    link: 'text-rose-700 decoration-rose-300 hover:bg-rose-50',
  },
  emerald: {
    chip: 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200',
    total: 'text-emerald-700',
    header: 'bg-emerald-50/60',
    link: 'text-emerald-700 decoration-emerald-300 hover:bg-emerald-50',
  },
  violet: {
    chip: 'bg-violet-100 text-violet-700 ring-1 ring-violet-200',
    total: 'text-violet-700',
    header: 'bg-violet-50/60',
    link: 'text-violet-700 decoration-violet-300 hover:bg-violet-50',
  },
  cyan: {
    chip: 'bg-cyan-100 text-cyan-700 ring-1 ring-cyan-200',
    total: 'text-cyan-700',
    header: 'bg-cyan-50/60',
    link: 'text-cyan-700 decoration-cyan-300 hover:bg-cyan-50',
  },
  lime: {
    chip: 'bg-lime-100 text-lime-700 ring-1 ring-lime-200',
    total: 'text-lime-700',
    header: 'bg-lime-50/60',
    link: 'text-lime-700 decoration-lime-300 hover:bg-lime-50',
  },
  orange: {
    chip: 'bg-orange-100 text-orange-700 ring-1 ring-orange-200',
    total: 'text-orange-700',
    header: 'bg-orange-50/60',
    link: 'text-orange-700 decoration-orange-300 hover:bg-orange-50',
  },
  fuchsia: {
    chip: 'bg-fuchsia-100 text-fuchsia-700 ring-1 ring-fuchsia-200',
    total: 'text-fuchsia-700',
    header: 'bg-fuchsia-50/60',
    link: 'text-fuchsia-700 decoration-fuchsia-300 hover:bg-fuchsia-50',
  },
  zinc: {
    chip: 'bg-zinc-100 text-zinc-700 ring-1 ring-zinc-200',
    total: 'text-zinc-700',
    header: 'bg-zinc-50/60',
    link: 'text-zinc-700 decoration-zinc-300 hover:bg-zinc-50',
  },
};

const KIND_TINT: Record<OpsActionableKind, Tint> = {
  flight: 'sky',
  hotel: 'amber',
  cruise: 'cyan',
  activity: 'rose',
  transfer: 'emerald',
  visa: 'violet',
  insurance: 'lime',
  meal: 'orange',
  fixed_departure: 'fuchsia',
  other: 'zinc',
};

/** Fixed departures first: the package is the booking, the rest hangs off it. */
export const KIND_ORDER: OpsActionableKind[] = [
  'fixed_departure',
  'flight',
  'hotel',
  'cruise',
  'activity',
  'transfer',
  'visa',
  'insurance',
  'meal',
  'other',
];

const fmtDate = (d: Date | null): string =>
  d ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const fmtIso = (iso: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  return Number.isNaN(d.getTime()) ? iso : fmtDate(d);
};
const fmtTime = (iso: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
};
const money = (n: number, c: string): string =>
  `${c === 'INR' ? '₹' : `${c} `}${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n)}`;
const title = (s: string | null): string =>
  s
    ? s
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : '-';
const pax = (i: OpsCaseItem): string => {
  const p = i.details?.pax;
  if (!p) return '-';
  return `${p.adults}A${p.children ? ` + ${p.children}C` : ''}${p.infants ? ` + ${p.infants}I` : ''}`;
};

/** Summary columns per kind: label + cell renderer. */
const COLUMNS: Record<
  OpsActionableKind,
  Array<{ h: string; cell: (i: OpsCaseItem) => React.ReactNode }>
> = {
  flight: [
    {
      h: 'Date',
      cell: (i) =>
        fmtIso(i.details?.flight?.outbound[0]?.departureAt ?? null) || fmtDate(i.startDate),
    },
    {
      h: 'Airline / flight',
      cell: (i) => {
        const s = i.details?.flight?.outbound[0];
        return s
          ? `${s.airlineName ?? s.airline ?? ''}${s.flightNumber ? ` ${s.airline ?? ''} ${s.flightNumber}` : ''}`.trim() ||
              i.title
          : i.title;
      },
    },
    { h: 'From', cell: (i) => i.details?.flight?.outbound[0]?.origin ?? i.fromLocation ?? '-' },
    {
      h: 'To',
      cell: (i) => {
        const segs = i.details?.flight?.outbound ?? [];
        return segs[segs.length - 1]?.destination ?? i.toLocation ?? '-';
      },
    },
    { h: 'Dep', cell: (i) => fmtTime(i.details?.flight?.outbound[0]?.departureAt ?? null) },
    {
      h: 'Arr',
      cell: (i) => {
        const segs = i.details?.flight?.outbound ?? [];
        return fmtTime(segs[segs.length - 1]?.arrivalAt ?? null);
      },
    },
    { h: 'Pax', cell: pax },
  ],
  hotel: [
    { h: 'Hotel', cell: (i) => i.details?.hotel?.hotelName ?? i.title },
    { h: 'Check-in', cell: (i) => fmtIso(i.details?.hotel?.checkIn ?? null) },
    { h: 'Check-out', cell: (i) => fmtIso(i.details?.hotel?.checkOut ?? null) },
    { h: 'Nights', cell: (i) => i.details?.hotel?.nights ?? '-' },
    {
      h: 'Room',
      cell: (i) =>
        i.details?.hotel?.roomName ??
        i.details?.hotel?.roomType ??
        i.details?.hotel?.occupancy ??
        '-',
    },
    { h: 'Meal', cell: (i) => title(i.details?.hotel?.mealPlan ?? null) },
    { h: 'Guests', cell: pax },
  ],
  cruise: [
    {
      h: 'Ship / cabin',
      cell: (i) =>
        [i.details?.cruise?.shipName ?? i.title, i.details?.cruise?.cabinName]
          .filter(Boolean)
          .join(' · '),
    },
    { h: 'Check-in', cell: (i) => fmtIso(i.details?.cruise?.checkIn ?? null) },
    { h: 'Check-out', cell: (i) => fmtIso(i.details?.cruise?.checkOut ?? null) },
    { h: 'Nights', cell: (i) => i.details?.cruise?.nights ?? '-' },
    { h: 'Board', cell: (i) => title(i.details?.cruise?.boardBasis ?? null) },
    { h: 'Guests', cell: pax },
  ],
  activity: [
    { h: 'Date', cell: (i) => fmtDate(i.startDate) },
    { h: 'Activity', cell: (i) => i.details?.activity?.activityName ?? i.title },
    { h: 'Ticket', cell: (i) => i.details?.activity?.variantName ?? '-' },
    {
      h: 'Time',
      cell: (i) => i.details?.startTime ?? i.details?.activity?.pickupTime ?? '-',
    },
    {
      h: 'Transfer',
      cell: (i) => {
        const m = i.details?.activity?.transferMode;
        return !m || m === 'NONE' ? 'No' : title(m);
      },
    },
    { h: 'Pax', cell: pax },
  ],
  transfer: [
    { h: 'Date', cell: (i) => fmtDate(i.startDate) },
    { h: 'Route', cell: (i) => transferRouteLabel(i) },
    { h: 'Pickup', cell: (i) => i.details?.transfer?.pickupTime ?? '-' },
    {
      h: 'Vehicle',
      cell: (i) =>
        [
          i.details?.transfer?.vehicleType,
          i.details?.transfer?.serviceType ? title(i.details.transfer.serviceType) : null,
        ]
          .filter(Boolean)
          .join(' · ') || '-',
    },
    { h: 'Pax', cell: pax },
  ],
  visa: [
    { h: 'Visa', cell: (i) => i.details?.visa?.visaName ?? i.title },
    { h: 'Type', cell: (i) => title(i.details?.visa?.visaType ?? null) },
    { h: 'Processing', cell: (i) => title(i.details?.visa?.processingMode ?? null) },
    { h: 'Applicants', cell: pax },
  ],
  insurance: [
    { h: 'Policy', cell: (i) => i.details?.insurance?.name ?? i.title },
    { h: 'Cover', cell: (i) => i.details?.insurance?.coverageAmountLabel ?? '-' },
    {
      h: 'Trip',
      cell: (i) => `${fmtDate(i.startDate)}${i.endDate ? ` - ${fmtDate(i.endDate)}` : ''}`,
    },
    { h: 'Insured', cell: pax },
  ],
  meal: [
    { h: 'Date', cell: (i) => fmtDate(i.startDate) },
    { h: 'Meal', cell: (i) => i.details?.meal?.name ?? i.title },
    { h: 'Restaurant', cell: (i) => i.details?.meal?.restaurantName ?? '-' },
    { h: 'Time', cell: (i) => i.details?.startTime ?? '-' },
    { h: 'Covers', cell: pax },
  ],
  fixed_departure: [
    { h: 'Departs', cell: (i) => fmtDate(i.startDate) },
    { h: 'Returns', cell: (i) => (i.endDate ? fmtDate(i.endDate) : '-') },
    { h: 'Package', cell: (i) => i.title },
    { h: 'Pax', cell: pax },
  ],
  other: [
    { h: 'Date', cell: (i) => fmtDate(i.startDate) },
    { h: 'Item', cell: (i) => i.title },
    { h: 'Pax', cell: pax },
  ],
};

const SectionHeader = ({
  kind,
  count,
  issued,
  total,
  currency,
}: {
  kind: OpsActionableKind;
  count: number;
  issued: number;
  total: number;
  currency: string;
}): React.JSX.Element => {
  const t = TINT[KIND_TINT[kind]];
  const Icon = KIND_META[kind].icon;
  return (
    <header
      className={cn(
        'flex items-center justify-between gap-3 border-b border-zinc-100 px-4 py-3',
        t.header,
      )}
    >
      <div className="flex items-center gap-3">
        <span
          className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full', t.chip)}
          aria-hidden
        >
          <Icon className="h-4 w-4" />
        </span>
        <h3 className="text-base font-semibold tracking-tight text-zinc-900">
          {KIND_META[kind].plural}
          <span className="ml-1.5 font-medium text-zinc-500 tabular-nums">({count})</span>
        </h3>
        <span
          className={cn(
            'rounded-md px-1.5 py-0.5 text-[11px] font-semibold',
            issued === count
              ? 'bg-emerald-100 text-emerald-800'
              : 'bg-white/80 text-zinc-600 ring-1 ring-zinc-200',
          )}
        >
          {issued} / {count} issued
        </span>
      </div>
      <span className={cn('font-mono text-base font-bold tabular-nums', t.total)}>
        {money(total, currency)}
      </span>
    </header>
  );
};

const KindSection = ({
  kind,
  items,
  canWork,
  openIds,
  onToggle,
  startIndex,
}: {
  kind: OpsActionableKind;
  items: OpsCaseItem[];
  canWork: boolean;
  openIds: Set<string>;
  onToggle: (id: string) => void;
  /** Serial of this section's first row: numbering runs across every item
   *  to book on the case, not per section. */
  startIndex: number;
}): React.JSX.Element => {
  const cols = COLUMNS[kind];
  const currency = items[0]?.quotedCurrency ?? 'INR';
  // Package-included rows (quoted 0) are listed but are not work.
  const bookable = items.filter((i) => !i.bundled);
  const total = items.reduce((s, i) => s + i.quotedCost, 0);
  const issued = bookable.filter((i) => i.status === 'ISSUED').length;
  return (
    <section
      id={`ops-${kind}`}
      className="scroll-mt-4 overflow-hidden rounded-xl border border-zinc-200 bg-white"
    >
      <SectionHeader
        kind={kind}
        count={bookable.length}
        issued={issued}
        total={total}
        currency={currency}
      />
      <div className="overflow-x-auto">
        {/* Columns take the width their content needs (no wrapped dates /
            badges / headers); a narrow screen scrolls the section sideways. */}
        <table className="w-full min-w-max text-sm [&>tbody>tr>td]:whitespace-nowrap [&>thead>tr>th]:whitespace-nowrap">
          <thead>
            <tr className="border-b border-zinc-100 bg-zinc-50/50 text-left">
              <th className="w-8 px-3 py-2.5" />
              <th className="w-10 px-2 py-2.5 text-xs font-medium tracking-wide text-zinc-500 uppercase">
                #
              </th>
              {cols.map((c, ci) => (
                <th
                  key={c.h}
                  className={cn(
                    'px-4 py-2.5 text-xs font-medium tracking-wide text-zinc-500 uppercase',
                    // The item's name: wide enough to read on one line.
                    ci === 0 && 'min-w-[280px]',
                  )}
                >
                  {c.h}
                </th>
              ))}
              <th className="px-4 py-2.5 text-xs font-medium tracking-wide text-zinc-500 uppercase">
                Status
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-medium tracking-wide text-zinc-500 uppercase">
                Quoted
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-medium tracking-wide text-zinc-500 uppercase">
                QC price
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-medium tracking-wide text-zinc-500 uppercase">
                Booked
              </th>
              <th className="px-4 py-2.5 text-xs font-medium tracking-wide text-zinc-500 uppercase">
                Hold no.
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-medium tracking-wide text-zinc-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              const open = openIds.has(item.componentId);
              const st = STATUS_META[item.status];
              return (
                <React.Fragment key={item.componentId}>
                  <tr
                    className={cn(
                      'cursor-pointer border-b border-zinc-100 transition-colors hover:bg-zinc-50/80',
                      open && 'bg-zinc-50',
                    )}
                    onClick={() => onToggle(item.componentId)}
                    aria-expanded={open}
                  >
                    <td className="px-3 py-3 text-zinc-400">
                      <ChevronDown
                        className={cn('h-4 w-4 transition-transform', open && 'rotate-180')}
                      />
                    </td>
                    <td className="px-2 py-3 text-zinc-500 tabular-nums">
                      {startIndex + index + 1}.
                    </td>
                    {cols.map((c) => (
                      <td key={c.h} className="px-4 py-3 text-zinc-800">
                        {c.cell(item)}
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      {item.bundled ? (
                        <UzBadge
                          tone="secondary"
                          title="Included in the package - nothing to book on its own"
                        >
                          In package
                        </UzBadge>
                      ) : (
                        <UzBadge tone={st.tone}>{st.label}</UzBadge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-zinc-900 tabular-nums">
                      {money(item.quotedCost, item.quotedCurrency)}
                    </td>
                    {/* The price ops got from the supplier (entered on hold). */}
                    <td className="px-4 py-3 text-right font-mono text-zinc-900 tabular-nums">
                      {item.qcPrice != null ? (
                        money(item.qcPrice, item.currency)
                      ) : (
                        <span className="text-zinc-300">-</span>
                      )}
                    </td>
                    {/* What Accounts actually paid the supplier. */}
                    <td className="px-4 py-3 text-right font-mono text-zinc-900 tabular-nums">
                      {item.fundAmount != null ? (
                        money(item.fundAmount, item.fundCurrency ?? item.currency)
                      ) : (
                        <span className="text-zinc-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-700">
                      {item.holdReference ?? <span className="text-zinc-300">-</span>}
                    </td>
                    {/* Act straight from the row; the buttons stop the row toggle. A
                        round trip booked as one ticket acts on its outbound leg only. */}
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {item.bundleRole === 'return' ? (
                        <span className="text-[11px] text-zinc-400">with outbound</span>
                      ) : (
                        <ItemActions item={item} caseId={item.caseId} canWork={canWork} compact />
                      )}
                    </td>
                  </tr>
                  {open ? (
                    <tr className="border-b border-zinc-100 bg-zinc-50/40">
                      <td colSpan={cols.length + 8} className="px-4 py-3 !whitespace-normal">
                        <ul>
                          <ItemCard
                            item={item}
                            caseId={item.caseId}
                            canWork={canWork}
                            details={<BookingDetails item={item} />}
                          />
                        </ul>
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

/** Jump links beside the page subheading, one per kind present. */
export const BookingJumpNav = ({ items }: { items: OpsCaseItem[] }): React.JSX.Element => {
  // Package-included rows are not bookings to make.
  const work = items.filter((i) => !i.bundled);
  const present = KIND_ORDER.filter((k) =>
    work.some((i) => actionableKindOf(i.kind, i.vasKind) === k),
  );
  return (
    <nav aria-label="Jump to booking section" className="flex flex-wrap items-center text-sm">
      {/* How many bookings there are to make in all, then one link per kind. */}
      <span className="px-1.5 py-0.5 font-medium text-zinc-700">
        Total <span className="font-semibold tabular-nums">{work.length}</span>
      </span>
      {present.map((k) => {
        const n = work.filter((i) => actionableKindOf(i.kind, i.vasKind) === k).length;
        return (
          <React.Fragment key={k}>
            <span className="px-1.5 text-zinc-300" aria-hidden>
              ·
            </span>
            <button
              type="button"
              onClick={() =>
                document
                  .getElementById(`ops-${k}`)
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
              className={cn(
                'cursor-pointer rounded-md px-1.5 py-0.5 font-medium underline decoration-dotted underline-offset-4 transition-colors',
                TINT[KIND_TINT[k]].link,
              )}
            >
              {KIND_META[k].plural} <span className="font-semibold tabular-nums">{n}</span>
            </button>
          </React.Fragment>
        );
      })}
    </nav>
  );
};

/** Every item acts on its own desk case (`item.caseId`), not the page's. */
export const OpsBookingSections = ({
  items,
  canWork,
}: {
  items: OpsCaseItem[];
  canWork: boolean;
}): React.JSX.Element => {
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const toggle = (id: string): void =>
    setOpenIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const groups = KIND_ORDER.map((k) => ({
    kind: k,
    items: items.filter((i) => actionableKindOf(i.kind, i.vasKind) === k),
  })).filter((g) => g.items.length);
  // Running serial across sections: Flights 1-2, Hotels 3, Cruises 4 ...
  const startIndexByKind = new Map<OpsActionableKind, number>();
  let running = 0;
  for (const g of groups) {
    startIndexByKind.set(g.kind, running);
    running += g.items.length;
  }

  if (!groups.length) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-200 px-4 py-8 text-center text-sm text-zinc-500">
        The converted itinerary has no bookable items.
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <KindSection
          key={g.kind}
          kind={g.kind}
          items={g.items}
          canWork={canWork}
          openIds={openIds}
          onToggle={toggle}
          startIndex={startIndexByKind.get(g.kind) ?? 0}
        />
      ))}
    </div>
  );
};
